import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  documentsApi,
  profileApi,
  qualificationsApi,
  recordsApi,
  referencesApi,
  workExperienceApi,
} from '../../services/backendApi';
import {
  Alert,
  Avatar,
  Button,
  Card,
  CardBody,
  SitePill,
  Spinner,
  VerificationBadge,
  VerifyModal,
} from '../../components';
import type { VerifyTarget } from '../../components';
import { formatDate, formatLabel, getFieldOfWorkLabel, sortQualificationsByIssueDate } from '../../utils';
import { isAtLeastConfirmed, passportItemAnchor, recordStatus } from '../../utils/verification';
import type {
  Document,
  ItemVerificationStatus,
  ProfessionalReference,
  Profile,
  Qualification,
  RecordKind,
  WorkExperience,
} from '../../types';

type Tab = 'profile' | 'qualifications' | 'experience' | 'references' | 'documents';
type ReviewMode = 'manager' | 'verifier';

const TABS: { id: Tab; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'qualifications', label: 'Qualifications' },
  { id: 'experience', label: 'Experience' },
  { id: 'references', label: 'References' },
  { id: 'documents', label: 'Official Documents' },
];

function isTab(value: string | null): value is Tab {
  return TABS.some(tab => tab.id === value);
}

const OFFICIAL_SLOTS: { type: Document['document_type']; label: string }[] = [
  { type: 'id_document', label: 'Identity Document' },
  { type: 'police_clearance', label: 'Police Clearance' },
];

interface EmployeeData {
  profile: Profile;
  qualifications: Qualification[];
  workExperience: WorkExperience[];
  references: ProfessionalReference[];
  documents: Document[];
}

function mergeStatus<T extends { id: string }>(
  items: T[],
  id: string,
  patch: Record<string, unknown>,
): T[] {
  return items.map(item => (item.id === id ? { ...item, ...patch } : item));
}

export const WorkerPassport: React.FC<{ mode?: ReviewMode }> = ({ mode = 'manager' }) => {
  const { workerId } = useParams<{ workerId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const backPath = mode === 'verifier' ? '/verifier/workers' : '/manager/workers';
  const focusItemId = searchParams.get('item');

  const [employee, setEmployee] = useState<EmployeeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const tab = searchParams.get('tab');
    return isTab(tab) ? tab : 'profile';
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<VerifyTarget | null>(null);

  useEffect(() => {
    const fetchEmployee = async () => {
      if (!workerId) return;
      setLoading(true);
      setError(null);
      try {
        const profile = await profileApi.getById(workerId);
        if (profile.has_profile === false || !profile.user_id) {
          setEmployee({
            profile,
            qualifications: [],
            workExperience: [],
            references: [],
            documents: [],
          });
        } else {
          const authUserId = profile.user_id;
          const [qualifications, workExperience, references, documents] = await Promise.all([
            qualificationsApi.getForWorker(authUserId),
            workExperienceApi.getForWorker(authUserId),
            referencesApi.getForWorker(authUserId),
            documentsApi.getForWorker(authUserId),
          ]);
          setEmployee({
            profile,
            qualifications: sortQualificationsByIssueDate(qualifications ?? []),
            workExperience: workExperience ?? [],
            references: references ?? [],
            documents: documents ?? [],
          });
        }
      } catch {
        setError('Failed to load employee profile');
      }
      setLoading(false);
    };
    fetchEmployee();
  }, [workerId]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (isTab(tab)) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (!focusItemId || loading || !employee) return;
    const t = window.setTimeout(() => {
      document.getElementById(passportItemAnchor(focusItemId))?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 80);
    return () => window.clearTimeout(t);
  }, [focusItemId, loading, employee, activeTab]);

  const applyPatch = (kind: RecordKind, id: string, patch: Record<string, unknown>) => {
    setEmployee(current => {
      if (!current) return current;
      if (kind === 'qualification') {
        return { ...current, qualifications: mergeStatus(current.qualifications, id, patch) };
      }
      if (kind === 'reference') {
        return { ...current, references: mergeStatus(current.references, id, patch) };
      }
      if (kind === 'work_experience') {
        return { ...current, workExperience: mergeStatus(current.workExperience, id, patch) };
      }
      return { ...current, documents: mergeStatus(current.documents, id, patch) };
    });
  };

  const confirmRecord = async (kind: RecordKind, id: string) => {
    setActingOn(id);
    setActionError(null);
    try {
      const updated = await recordsApi.confirm(kind, id);
      applyPatch(kind, id, updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to confirm record');
    } finally {
      setActingOn(null);
    }
  };

  const submitVerify = async (basis: string) => {
    if (!verifyTarget) return;
    setActingOn(verifyTarget.id);
    setActionError(null);
    try {
      const updated = await recordsApi.verify(verifyTarget.kind, verifyTarget.id, basis);
      applyPatch(verifyTarget.kind, verifyTarget.id, updated);
      setVerifyTarget(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to verify record');
    } finally {
      setActingOn(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Alert variant="danger">{error || 'Employee not found'}</Alert>
        <Button variant="secondary" onClick={() => navigate(backPath)}>
          Back
        </Button>
      </div>
    );
  }

  const { profile, qualifications, workExperience, references, documents } = employee;
  const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Employee';
  const officialCount = documents.filter(
    d => d.document_type === 'id_document' || d.document_type === 'police_clearance',
  ).length;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={() => navigate(backPath)}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            src={profile.photo_url}
            alt={name}
            size="md"
            fallback={profile.first_name?.charAt(0) || '?'}
            className="shrink-0"
          />
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 break-words">{name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-0.5">
              {profile.field_of_work && (
                <p className="text-sm text-gray-500">{getFieldOfWorkLabel(profile.field_of_work)}</p>
              )}
              <SitePill
                tenantId={profile.tenant_id}
                siteId={profile.tenant_site_id}
                showTenant={mode === 'verifier'}
              />
            </div>
          </div>
        </div>
      </div>

      {profile.has_profile === false ? (
        <Card>
          <CardBody>
            <p className="text-sm text-gray-700">
              {name} is assigned to this site, but has not created a Passport yet.
            </p>
          </CardBody>
        </Card>
      ) : (
      <div className="space-y-6">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.id === 'qualifications' && qualifications.length > 0 && (
                <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                  {qualifications.length}
                </span>
              )}
              {tab.id === 'experience' && workExperience.length > 0 && (
                <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                  {workExperience.length}
                </span>
              )}
              {tab.id === 'references' && references.length > 0 && (
                <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                  {references.length}
                </span>
              )}
              {tab.id === 'documents' && officialCount > 0 && (
                <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                  {officialCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {actionError && (
        <Alert variant="danger" onClose={() => setActionError(null)}>{actionError}</Alert>
      )}

      {activeTab === 'profile' && <ProfileTab profile={profile} />}
      {activeTab === 'qualifications' && (
        <QualificationsTab
          qualifications={qualifications}
          documents={documents}
          mode={mode}
          actingOn={actingOn}
          focusItemId={focusItemId}
          onConfirm={id => confirmRecord('qualification', id)}
          onVerify={qual =>
            setVerifyTarget({
              kind: 'qualification',
              id: qual.id,
              title: qual.title,
              status: recordStatus(qual),
            })
          }
        />
      )}
      {activeTab === 'experience' && (
        <ExperienceTab
          workExperience={workExperience}
          mode={mode}
          actingOn={actingOn}
          focusItemId={focusItemId}
          onConfirm={id => confirmRecord('work_experience', id)}
          onVerify={exp =>
            setVerifyTarget({
              kind: 'work_experience',
              id: exp.id,
              title: `${exp.job_title} at ${exp.employer}`,
              status: recordStatus(exp),
            })
          }
        />
      )}
      {activeTab === 'references' && (
        <ReferencesTab
          references={references}
          documents={documents}
          mode={mode}
          actingOn={actingOn}
          focusItemId={focusItemId}
          onConfirm={id => confirmRecord('reference', id)}
          onVerify={ref =>
            setVerifyTarget({
              kind: 'reference',
              id: ref.id,
              title: ref.reference_name,
              status: recordStatus(ref),
            })
          }
        />
      )}
      {activeTab === 'documents' && (
        <DocumentsTab
          documents={documents}
          mode={mode}
          actingOn={actingOn}
          focusItemId={focusItemId}
          onConfirm={id => confirmRecord('official_document', id)}
          onVerify={doc =>
            setVerifyTarget({
              kind: 'official_document',
              id: doc.id,
              title: doc.file_name,
              status: recordStatus(doc),
            })
          }
        />
      )}

      {verifyTarget && (
        <VerifyModal
          target={verifyTarget}
          submitting={actingOn === verifyTarget.id}
          error={actionError}
          onClose={() => setVerifyTarget(null)}
          onSubmit={submitVerify}
        />
      )}
      </div>
      )}
    </div>
  );
};

const ReviewActions: React.FC<{
  mode: ReviewMode;
  status?: ItemVerificationStatus;
  acting: boolean;
  confirmedAt?: string;
  verifiedAt?: string;
  basis?: string;
  onConfirm: () => void;
  onVerify: () => void;
}> = ({ mode, status, acting, confirmedAt, verifiedAt, basis, onConfirm, onVerify }) => {
  const resolved = recordStatus({ verification_status: status });
  return (
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      <VerificationBadge status={resolved} />
      {mode === 'manager' && !isAtLeastConfirmed(resolved) && (
        <Button size="sm" variant="success" loading={acting} disabled={acting} onClick={onConfirm}>
          Accept
        </Button>
      )}
      {mode === 'verifier' && resolved !== 'verified' && (
        <Button size="sm" loading={acting} disabled={acting} onClick={onVerify}>
          Verify
        </Button>
      )}
      {confirmedAt && (
        <p className="text-[10px] text-gray-400">Accepted {formatDate(confirmedAt)}</p>
      )}
      {verifiedAt && (
        <p className="text-[10px] text-gray-400">Verified {formatDate(verifiedAt)}</p>
      )}
      {basis && <p className="text-[10px] text-gray-500 max-w-48 text-right">{basis}</p>}
    </div>
  );
};

const EvidenceList: React.FC<{ documents: Document[] }> = ({ documents }) => {
  if (documents.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
        Supporting evidence
      </p>
      <div className="space-y-2">
        {documents.map(doc => (
          <a
            key={doc.id}
            href={doc.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-brand-600 hover:underline truncate"
          >
            {doc.file_name}
          </a>
        ))}
      </div>
    </div>
  );
};

const ProfileTab: React.FC<{ profile: Profile }> = ({ profile }) => (
  <Card>
    <CardBody>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {profile.bio && (
          <div className="sm:col-span-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Bio</p>
            <p className="text-sm text-gray-700">{profile.bio}</p>
          </div>
        )}
        {(profile.first_name || profile.last_name) && (
          <Field label="Full Name" value={[profile.first_name, profile.last_name].filter(Boolean).join(' ')} />
        )}
        {profile.field_of_work && (
          <Field label="Field of Work" value={getFieldOfWorkLabel(profile.field_of_work)} />
        )}
        {profile.contact_email && (
          <Field label="Email">
            <a href={`mailto:${profile.contact_email}`} className="text-brand-600 hover:underline text-sm">
              {profile.contact_email}
            </a>
          </Field>
        )}
        {profile.phone && (
          <Field label="Phone">
            <a href={`tel:${profile.phone}`} className="text-brand-600 hover:underline text-sm">
              {profile.phone}
            </a>
          </Field>
        )}
        {profile.date_of_birth && (
          <Field label="Date of Birth" value={formatDate(profile.date_of_birth)} />
        )}
        {profile.rsa_id_number && <Field label="RSA ID Number" value={profile.rsa_id_number} />}
        {profile.passport_no && <Field label="Passport Number" value={profile.passport_no} />}
      </div>
    </CardBody>
  </Card>
);

const FocusFrame: React.FC<{
  id: string;
  focusItemId?: string | null;
  className?: string;
  children: React.ReactNode;
}> = ({ id, focusItemId, className = '', children }) => {
  const focused = focusItemId === id;
  return (
    <div
      id={passportItemAnchor(id)}
      className={`${className} ${
        focused
          ? 'rounded-xl bg-brand-50/80 ring-2 ring-brand-400 ring-offset-2 p-4 scroll-mt-24'
          : ''
      }`}
    >
      {focused && (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-700 mb-2">
          Opened from queue
        </p>
      )}
      {children}
    </div>
  );
};

const QualificationsTab: React.FC<{
  qualifications: Qualification[];
  documents: Document[];
  mode: ReviewMode;
  actingOn: string | null;
  focusItemId?: string | null;
  onConfirm: (id: string) => void;
  onVerify: (qual: Qualification) => void;
}> = ({ qualifications, documents, mode, actingOn, focusItemId, onConfirm, onVerify }) => {
  if (qualifications.length === 0) return <EmptyState message="No qualifications added yet" />;
  return (
    <Card>
      <CardBody>
        <div className="space-y-4">
          {qualifications.map((qual, index) => (
            <FocusFrame
              key={qual.id}
              id={qual.id}
              focusItemId={focusItemId}
              className={index < qualifications.length - 1 && focusItemId !== qual.id ? 'pb-4 border-b border-gray-100' : ''}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{qual.title}</p>
                  <p className="text-sm text-gray-500">{qual.institution}</p>
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-400">
                    {qual.qualification_type && <span className="capitalize">{formatLabel(qual.qualification_type)}</span>}
                    {qual.issue_date && <span>Issued: {formatDate(qual.issue_date)}</span>}
                    {qual.expiry_date && (
                      <span className={new Date(qual.expiry_date) < new Date() ? 'text-red-500' : ''}>
                        Expires: {formatDate(qual.expiry_date)}
                      </span>
                    )}
                  </div>
                </div>
                <ReviewActions
                  mode={mode}
                  status={qual.verification_status}
                  acting={actingOn === qual.id}
                  confirmedAt={qual.confirmed_at}
                  verifiedAt={qual.verified_at}
                  basis={qual.verification_basis}
                  onConfirm={() => onConfirm(qual.id)}
                  onVerify={() => onVerify(qual)}
                />
              </div>
              <EvidenceList documents={documents.filter(doc => doc.qualification_id === qual.id)} />
            </FocusFrame>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

const ExperienceTab: React.FC<{
  workExperience: WorkExperience[];
  mode: ReviewMode;
  actingOn: string | null;
  focusItemId?: string | null;
  onConfirm: (id: string) => void;
  onVerify: (exp: WorkExperience) => void;
}> = ({ workExperience, mode, actingOn, focusItemId, onConfirm, onVerify }) => {
  if (workExperience.length === 0) return <EmptyState message="No work experience added yet" />;
  return (
    <Card>
      <CardBody>
        <div className="space-y-4">
          {workExperience.map((exp, index) => (
            <FocusFrame
              key={exp.id}
              id={exp.id}
              focusItemId={focusItemId}
              className={index < workExperience.length - 1 && focusItemId !== exp.id ? 'pb-4 border-b border-gray-100' : ''}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{exp.job_title}</p>
                  <p className="text-sm text-gray-500">
                    {exp.employer}{exp.location ? ` · ${exp.location}` : ''}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatDate(exp.start_date)} – {exp.is_current ? 'Present' : exp.end_date ? formatDate(exp.end_date) : ''}
                  </p>
                  {exp.description && <p className="text-sm text-gray-500 mt-1.5">{exp.description}</p>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {exp.is_current && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Current</span>
                  )}
                  <ReviewActions
                    mode={mode}
                    status={exp.verification_status}
                    acting={actingOn === exp.id}
                    confirmedAt={exp.confirmed_at}
                    verifiedAt={exp.verified_at}
                    basis={exp.verification_basis}
                    onConfirm={() => onConfirm(exp.id)}
                    onVerify={() => onVerify(exp)}
                  />
                </div>
              </div>
            </FocusFrame>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

const ReferencesTab: React.FC<{
  references: ProfessionalReference[];
  documents: Document[];
  mode: ReviewMode;
  actingOn: string | null;
  focusItemId?: string | null;
  onConfirm: (id: string) => void;
  onVerify: (ref: ProfessionalReference) => void;
}> = ({ references, documents, mode, actingOn, focusItemId, onConfirm, onVerify }) => {
  if (references.length === 0) return <EmptyState message="No references added yet" />;
  return (
    <Card>
      <CardBody>
        <div className="space-y-4">
          {references.map((ref, index) => (
            <FocusFrame
              key={ref.id}
              id={ref.id}
              focusItemId={focusItemId}
              className={index < references.length - 1 && focusItemId !== ref.id ? 'pb-4 border-b border-gray-100' : ''}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{ref.reference_name}</p>
                  {(ref.job_title || ref.company) && (
                    <p className="text-sm text-gray-500">
                      {[ref.job_title, ref.company].filter(Boolean).join(' at ')}
                    </p>
                  )}
                  {ref.relationship && (
                    <p className="text-xs text-gray-400 mt-0.5 capitalize">{ref.relationship}</p>
                  )}
                </div>
                <ReviewActions
                  mode={mode}
                  status={ref.verification_status}
                  acting={actingOn === ref.id}
                  confirmedAt={ref.confirmed_at}
                  verifiedAt={ref.verified_at}
                  basis={ref.verification_basis}
                  onConfirm={() => onConfirm(ref.id)}
                  onVerify={() => onVerify(ref)}
                />
              </div>
              <EvidenceList documents={documents.filter(doc => doc.reference_id === ref.id)} />
            </FocusFrame>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

const DocumentsTab: React.FC<{
  documents: Document[];
  mode: ReviewMode;
  actingOn: string | null;
  focusItemId?: string | null;
  onConfirm: (id: string) => void;
  onVerify: (doc: Document) => void;
}> = ({ documents, mode, actingOn, focusItemId, onConfirm, onVerify }) => {
  const slots = OFFICIAL_SLOTS.map(slot => ({
    ...slot,
    doc: documents.find(d => d.document_type === slot.type),
  }));
  if (!slots.some(s => s.doc)) return <EmptyState message="No official documents uploaded yet" />;

  return (
    <div className="space-y-4">
      {slots.map(({ type, label, doc }) => (
        <FocusFrame
          key={type}
          id={doc?.id ?? type}
          focusItemId={doc ? focusItemId : null}
        >
        <Card>
          <CardBody>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-medium text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5 capitalize">{formatLabel(type)}</p>
              </div>
              {doc ? (
                <ReviewActions
                  mode={mode}
                  status={doc.verification_status}
                  acting={actingOn === doc.id}
                  confirmedAt={doc.confirmed_at}
                  verifiedAt={doc.verified_at}
                  basis={doc.verification_basis}
                  onConfirm={() => onConfirm(doc.id)}
                  onVerify={() => onVerify(doc)}
                />
              ) : (
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not uploaded</span>
              )}
            </div>
            {doc ? (
              <a
                href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-brand-600 hover:underline"
              >
                {doc.file_name}
              </a>
            ) : (
              <p className="text-sm text-gray-400">Employee has not uploaded this document yet.</p>
            )}
          </CardBody>
        </Card>
        </FocusFrame>
      ))}
    </div>
  );
};

const Field: React.FC<{ label: string; value?: string; children?: React.ReactNode }> = ({
  label,
  value,
  children,
}) => (
  <div>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    {children ?? <p className="text-sm text-gray-900">{value || '—'}</p>}
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <Card>
    <CardBody>
      <p className="text-center text-gray-400 text-sm py-8">{message}</p>
    </CardBody>
  </Card>
);
